import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { usePartner } from '~/context/partner'
import { getStickyBanners } from '~/services/contentService'
import { ContentfulStickyBannerType } from '@reward-platform/shared-schemas'
import { mapPathToPromotionProduct } from '~/utils/promotion/promotionMapper'
import { useRouter } from 'next/router'
import { useChannel } from '~/context/analytics/useChannel'
import { useLocalisation } from '~/context/localisation'

const MAXIMUM_NUMBER_OF_STICKY_BANNERS = 3

export type StickyBannerType = 'SUCCESS' | 'WARNING' | 'ERROR' | 'INFO' | 'PROMOTION'

const getChannelToContentfulProduct = (channel: string): string[] => {
  const channelToContentfulProduct: {
    [key: string]: string
  } = {
    car_hire: 'CAR_HIRE',
    experiences: 'EXPERIENCE',
    flights: 'FLIGHT',
    hotels: 'HOTEL',
  }
  return channel.split(',').map((item) => channelToContentfulProduct[item])
}

const isProductInList = (
  banner: ContentfulStickyBannerType,
  channel?: string,
  product?: string
) => {
  if (channel) {
    const products = getChannelToContentfulProduct(channel)
    return banner.product?.some((item: string) => products.includes(item))
  }
  if (product) {
    return banner.product?.includes(product)
  }
  return false
}

const filterBannerByProduct = (
  banners: ContentfulStickyBannerType[],
  channel?: string,
  product?: string
) => {
  return banners.filter((banner) => {
    return (
      (banner.message && isProductInList(banner, channel, product)) ||
      (banner.message && !banner.product)
    )
  })
}

const useStickyBanner = ({ enabled = true } = {}) => {
  const { locale } = useLocalisation()
  const router = useRouter()
  const partner = usePartner()
  const product = mapPathToPromotionProduct(router.asPath)
  const channel = useChannel()

  const [messages, setMessages] = useState<Array<ContentfulStickyBannerType> | []>([])

  const { data: stickyBanners, isFetched: stickyBannersFetched } = useQuery({
    queryKey: ['stickyBanners', partner.code, locale],
    queryFn: () => getStickyBanners(partner.code, locale),
    enabled,
    meta: {
      notifyUser: false,
      errorMessage: 'Content Error - could not get sticky banners',
    },
  })

  useEffect(() => {
    if (stickyBannersFetched && stickyBanners) {
      const filteredBanners = filterBannerByProduct(stickyBanners, channel, product)
      setMessages(
        filteredBanners.length > MAXIMUM_NUMBER_OF_STICKY_BANNERS
          ? filteredBanners.slice(0, MAXIMUM_NUMBER_OF_STICKY_BANNERS)
          : filteredBanners
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyBannersFetched, stickyBanners, product, channel])

  return messages
}

export default useStickyBanner
